import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // When loading with ?variant=slug, variant picker will sync and dispatch VariantUpdateEvent.
    // We handle it normally – #findFirstSlideIndexForVariant slides to the correct color image.
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * Auto-slides to the first occurrence of the selected color's image.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   * @param {number} [retryCount] - Retry attempt (used internally).
   */
  #handleVariantUpdate = (event, retryCount = 0) => {
    const source = event.detail.data?.html;
    const variant = event.detail.resource;

    if (source) {
      const newMediaGallery = source.querySelector('media-gallery');
      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
        // Re-dispatch without HTML so the new gallery runs jump logic (alt/featured)
        requestAnimationFrame(() => {
          newMediaGallery.dispatchEvent(
            new CustomEvent(ThemeEvents.variantUpdate, {
              detail: { resource: variant, data: {} },
              bubbles: true,
            })
          );
        });
        return;
      }
    }

    // Use grid items when presentation is grid; otherwise slideshow slides (carousel)
    const isGrid = this.dataset.presentation === 'grid';
    const slideshow = this.refs?.slideshow;
    const items = isGrid
      ? (this.refs?.media ?? [])
      : (slideshow?.slides ?? this.refs?.media ?? []);

    if (!items?.length && retryCount < 5) {
      // Section may have been morphed; refs need time to refresh (longer on mobile)
      const baseMs = isMobileBreakpoint() ? 80 : 50;
      const delay = baseMs * (retryCount + 1);
      setTimeout(() => this.#handleVariantUpdate(event, retryCount + 1), delay);
      return;
    }

    // When section was morphed, server returns correct order – slide 0 is first.
    // Otherwise find the slide that matches the variant's colour/featured image.
    const morphedSection = event.detail.data?.morphedSection === true;
    const index = morphedSection
      ? 0
      : this.#findFirstSlideIndexForVariant(items, variant);

    if (index >= 0) {
      const targetSlide = items[index];
      if (isGrid && this.refs?.media?.[index]) {
        this.refs.media[index].scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      } else if (slideshow?.select) {
        // Use slideshow.select to slide to the variant's image (carousel)
        slideshow.select(index, undefined, { animate: false });
        if (morphedSection && targetSlide && retryCount === 0) {
          // After morph, retry once – refs may need extra time on slower devices
          setTimeout(() => this.#handleVariantUpdate(event, 1), 150);
        }
      } else if (targetSlide) {
        targetSlide.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      }
    } else if (retryCount < 5) {
      const baseMs = isMobileBreakpoint() ? 80 : 50;
      setTimeout(() => this.#handleVariantUpdate(event, retryCount + 1), baseMs * (retryCount + 1));
    }
  };

  /**
   * Finds the first slide index whose image matches the variant's colour.
   * Matches by: 1) featured_media id, 2) data-color-slug (from variant/alt), 3) img alt, 4) fallback 0.
   * @param {Element[]} items - Slides or media elements (with data-media-id, data-color-slug)
   * @param {Object} variant - Variant with option1/2/3, featured_media
   * @returns {number} Index or -1
   */
  #findFirstSlideIndexForVariant(items, variant) {
    if (!items?.length) return -1;

    const getMediaId = (el) => {
      const id = el?.dataset?.mediaId ?? el?.querySelector?.('[data-media-id]')?.dataset?.mediaId;
      return id != null ? String(id) : '';
    };
    const getColorSlug = (el) => {
      const slug = el?.dataset?.colorSlug ?? el?.querySelector?.('[data-color-slug]')?.dataset?.colorSlug;
      return slug ? String(slug).trim().toLowerCase() : '';
    };

    // 1) Match by featured media id (most reliable when available)
    const featuredMediaId =
      variant?.featured_media?.id ??
      variant?.featured_media?.preview_image?.id ??
      variant?.featured_image?.id ??
      variant?.image_id;
    if (featuredMediaId != null) {
      const needle = String(featuredMediaId);
      const idx = items.findIndex((el) => getMediaId(el) === needle);
      if (idx >= 0) return idx;
    }

    // 2) Match by data-color-slug (set from variant option1 or media.alt in Liquid)
    const optionValues = [
      variant?.option1,
      variant?.option2,
      variant?.option3,
    ]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase().replace(/\s+/g, '-'));
    for (const optSlug of optionValues) {
      if (!optSlug) continue;
      const idx = items.findIndex((el) => {
        const slug = getColorSlug(el);
        if (!slug) return false;
        return slug === optSlug || slug.includes(optSlug) || optSlug.includes(slug);
      });
      if (idx >= 0) return idx;
    }

    // 3) Match by img alt – support "Product - Color", "Product in Color", or alt containing color
    const optValsNorm = [
      variant?.option1,
      variant?.option2,
      variant?.option3,
    ]
      .filter(Boolean)
      .map((v) => String(v).trim().toLowerCase().replace(/\s+/g, ' '));
    for (const optVal of optValsNorm) {
      if (!optVal) continue;
      const optSlug = optVal.replace(/\s+/g, '-');
      const optNorm = optVal.replace(/-/g, ' ');
      const idx = items.findIndex((el) => {
        const img = el?.querySelector?.('img[alt]');
        if (!img?.alt) return false;
        const alt = String(img.alt).trim().toLowerCase();
        const afterDash = alt.split(/\s*[-–]\s*/).pop()?.trim() ?? '';
        const afterIn = alt.split(/\s+in\s+/).pop()?.trim() ?? '';
        return (
          alt === optVal ||
          alt === optSlug ||
          alt.includes(optVal) ||
          alt.includes(optSlug) ||
          alt.includes(optNorm) ||
          afterDash === optVal ||
          afterDash === optSlug ||
          afterIn === optVal ||
          afterIn === optSlug ||
          optVal.includes(afterDash.split(/\s+/)[0])
        );
      });
      if (idx >= 0) return idx;
    }

    // 4) Fallback – first slide
    return 0;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
